import React, { useContext, useEffect, useState } from 'react'
import { setCookie, deleteCookie, getCookie } from 'cookies-next'
import { jwtDecode } from 'jwt-decode'

import { axiosInstance } from 'api/axios'
import { authService } from 'api/services/auth'
import { User } from 'api/types/auth'
import { BrokerContact, GetBrokerContactresponseBody } from 'api/types/broker'
import { toast } from 'keep-react'
import { AppContext } from './AppContext'
import { fileService } from 'api/services/file'
import { brokerService } from 'api/services/broker'

// Extend AxiosRequestConfig to include _retry
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

export interface AuthContextType {
  accessToken: string | null
  currentUser: User | null
  setCurrentUser: (user: User | null) => void
  accessLevel: number | null
  setAccessLevel: (level: number | null) => void
  roleId: number | null
  setRoleId: (roleId: number | null) => void
  isSignedIn: boolean
  brokerContacts: BrokerContact[] | null
  setBrokerContacts: (contacts: BrokerContact[] | null) => void
  currentBroker: BrokerContact | null
  setCurrentBroker: (broker: BrokerContact | null) => void
  currentBrokerLogo?: string | null
  setCurrentBrokerLogo?: (logo: string | null) => void
  login: ({ email, password }: any) => Promise<void>
  logout: () => void
}

export const AuthContext = React.createContext<AuthContextType>({
  accessToken: null,
  currentUser: null,
  setCurrentUser: () => {},
  accessLevel: null,
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  setAccessLevel: () => {},
  roleId: null,
  setRoleId: () => {},
  isSignedIn: false,
  brokerContacts: null,
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  setBrokerContacts: () => {},
  currentBroker: null,
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  setCurrentBroker: () => {},
  currentBrokerLogo: null,
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  setCurrentBrokerLogo: () => {},
  //eslint-disable-next-line @typescript-eslint/no-empty-function
  login: () => Promise.resolve(),
  logout: () => {},
})

export const AuthProvider = ({ children }: { children: any }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [accessLevel, setAccessLevel] = useState<number | null>(null)
  const [roleId, setRoleId] = useState<number | null>(null)
  const [currentBroker, setCurrentBroker] = useState<BrokerContact | null>(
    getCookie('currentBroker')
      ? JSON.parse(getCookie('currentBroker') as string)
      : null,
  )
  const [currentBrokerLogo, setCurrentBrokerLogo] = useState<string | null>(
    null,
  )
  const [isSignedIn, setIsSignedIn] = useState(
    getCookie('accessToken') ? true : false,
  )
  const [brokerContacts, setBrokerContacts] = useState<BrokerContact[] | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { setShowAccessMode } = useContext(AppContext)

  const getContacts = async () => {
    try {
      const response = (await brokerService.getBrokerContact({
        brokerNo: currentBroker ? currentBroker.BrokerNo : 0,
      })) as GetBrokerContactresponseBody
      setAccessLevel(Number(response.Contact.Access.AccessLevel))

      const trimmedContacts = response.Contact.Access.BrokerAccess.map(
        contact => ({
          ...contact,
          BrokerName: contact.BrokerName.trim(),
        }),
      ) as BrokerContact[]
      setBrokerContacts(trimmedContacts)
    } catch (error) {
      toast.error(
        'Problema ao obter os contactos, se o problema persistir contacte a Innovarisk.',
      )
      console.error('Get contacts error:', error)
    }
  }

  const login = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }) => {
    try {
      const accessToken = (await authService.login({
        email,
        password,
      })) as string
      const user = jwtDecode<User>(accessToken)
      _setData(user, accessToken)

      getContacts()
    } catch (error) {
      toast.error(
        'Problema com o login, username ou password errado ou inexistentes, se o problema persistir contacte a Innovarisk.',
      )
      console.error('Login error:', error)
    }
  }

  const logout = () => {
    setBrokerContacts(null)
    _deleteData()
  }

  const _setData = (user: User, token: string) => {
    const currentBrokerFromCookie = getCookie('currentBroker')
    if (!user || !token) return
    try {
      setCurrentUser(user)
      setCurrentBroker(
        currentBrokerFromCookie
          ? JSON.parse(currentBrokerFromCookie as string)
          : null,
      )
      setAccessToken(token)
      setIsSignedIn(true)
      setRoleId(parseInt(user.RoleId))
      setCookie('accessToken', token)
      setCookie('user', JSON.stringify(user))
    } catch (e) {
      console.error('Error setting auth data', e)
    }
  }

  const _deleteData = () => {
    setCurrentUser(null)
    setAccessToken(null)
    setIsSignedIn(false)
    setCurrentBroker(null)
    deleteCookie('accessToken')
    deleteCookie('user')
    deleteCookie('currentBroker')
  }

  useEffect(() => {
    const accessToken = getCookie('accessToken')
    const userRaw = getCookie('user')

    // Only initialize auth state when both user and token are present.
    if (accessToken && userRaw) {
      try {
        const userParsed = JSON.parse(userRaw as string)
        _setData(userParsed, accessToken as string)

        if (!accessLevel) {
          setTimeout(() => {
            getContacts()
          }, 500)
        }
      } catch (e) {
        // invalid cookie data, clear them
        console.warn('Auth init: invalid user cookie, clearing auth cookies')
        deleteCookie('accessToken')
        deleteCookie('user')
      }
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (currentBroker) {
      setCookie('currentBroker', JSON.stringify(currentBroker))

      const getLogo = async () => {
        try {
          const logo = await fileService.getFilesFromAzure({
            identifier: currentBroker.BrokerNo.toString(),
            type: 'logo',
          })
          setCurrentBrokerLogo(logo && logo.length > 0 ? logo[0].uri : null)
        } catch (e) {
          console.error('Error fetching broker logo', e)
        }
      }

      getLogo()
    } else {
      setShowAccessMode(true)
    }
  }, [currentBroker])

  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      config => {
        const token = getCookie('accessToken')
        if (token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Cleanup on unmount
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor)
    }
  }, [])

  useEffect(() => {
    const responseInterceptor = axiosInstance.interceptors.response.use(
      response => response,
      error => {
        try {
          if (error && error.response && error.response.status === 401) {
            _deleteData()
            logout()
          }
        } catch (e) {
          console.warn('Error dispatching auth:logout event', e)
        }

        return Promise.reject(error)
      },
    )

    // Cleanup on unmount
    return () => {
      axiosInstance.interceptors.request.eject(responseInterceptor)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        currentUser,
        setCurrentUser,
        accessLevel,
        setAccessLevel,
        roleId,
        setRoleId,
        isSignedIn,
        brokerContacts,
        setBrokerContacts,
        currentBroker,
        setCurrentBroker,
        currentBrokerLogo,
        setCurrentBrokerLogo,
        login,
        logout,
      }}
    >
      {!isLoading && children}
    </AuthContext.Provider>
  )
}
